<template>
  <div style="width: 95%; display: flex; flex-direction: column; align-items: center; justify-content: center;">
    <!-- STEP 1: Input AWBs + date range -->
    <div v-if="step === 'input'" style="width: 100%; max-width: 800px; margin-top: 20px;">
      <v-textarea
          v-model="awbText"
          label="LISTA AWB (uno per riga)"
          variant="outlined"
          rows="10"
          placeholder="Inserisci gli AWB da cercare, uno per riga"
      ></v-textarea>
      <div style="display: flex; column-gap: 15px; margin-top: 10px; align-items: center; flex-wrap: wrap; row-gap: 10px;">
        <v-dialog width="375px">
          <template v-slot:activator="{ props }">
            <v-btn
                v-bind="props"
                variant="outlined"
                text="DATA INIZIO"
                height="48px"
                width="200"
            ></v-btn>
          </template>
          <template v-slot:default="{ isActive }">
            <v-card title="DATA INIZIO">
              <div style="display: flex; justify-content: center; align-items: center;">
                <v-date-picker
                    style="border: 1px solid #d0d0d0 !important"
                    v-model="dataInizio"
                    :max="dataFine"
                ></v-date-picker>
              </div>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text="CANCELLA" @click="dataInizio = null" class="bg-red" variant="outlined"></v-btn>
                <v-btn text="OK" @click="isActive.value = false" class="bg-green" variant="outlined"></v-btn>
              </v-card-actions>
            </v-card>
          </template>
        </v-dialog>
        <small>{{ formatDate(dataInizio) }}</small>

        <v-dialog width="375px">
          <template v-slot:activator="{ props }">
            <v-btn
                v-bind="props"
                variant="outlined"
                text="DATA FINE"
                height="48px"
                width="200"
            ></v-btn>
          </template>
          <template v-slot:default="{ isActive }">
            <v-card title="DATA FINE">
              <div style="display: flex; justify-content: center; align-items: center;">
                <v-date-picker
                    style="border: 1px solid #d0d0d0 !important"
                    v-model="dataFine"
                    :min="dataInizio"
                ></v-date-picker>
              </div>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text="CANCELLA" @click="dataFine = null" class="bg-red" variant="outlined"></v-btn>
                <v-btn text="OK" @click="isActive.value = false" class="bg-green" variant="outlined"></v-btn>
              </v-card-actions>
            </v-card>
          </template>
        </v-dialog>
        <small>{{ formatDate(dataFine) }}</small>

        <v-btn
            class="bg-blue"
            variant="outlined"
            height="48px"
            @click="searchAwbs"
            :loading="loading"
            :disabled="!canSearch"
        >CERCA
        </v-btn>
      </div>
      <v-switch
          v-model="nascondiColli"
          label="NASCONDI COLLI"
          color="#2196f3"
          hide-details
          style="margin-top: 10px;"
      ></v-switch>
    </div>

    <!-- STEP 2: Preview results -->
    <div v-if="step === 'preview'" style="width: 100%; margin-top: 20px;">
      <!-- Missing AWBs -->
      <v-alert
          v-if="missing.length > 0"
          type="warning"
          variant="tonal"
          style="margin-bottom: 20px;"
      >
        <strong>AWB NON TROVATI ({{ missing.length }}):</strong>
        <div style="margin-top: 5px;">{{ missing.join(', ') }}</div>
      </v-alert>

      <!-- Matches table -->
      <div v-if="matches.length > 0">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
          <span><strong>{{ matches.length }}</strong> colli trovati</span>
          <div style="display: flex; column-gap: 10px;">
            <v-btn variant="outlined" @click="step = 'input'">INDIETRO</v-btn>
            <v-btn
                class="bg-green"
                variant="outlined"
                @click="selectAll"
            >SELEZIONA TUTTI
            </v-btn>
            <v-btn
                class="bg-red"
                variant="outlined"
                @click="confirmRemoval"
                :disabled="selected.length === 0"
                :loading="deleting"
            >RIMUOVI SELEZIONATI ({{ selected.length }})
            </v-btn>
          </div>
        </div>
        <v-data-table
            v-model="selected"
            :headers="headers"
            :items="matches"
            show-select
            return-object
            item-value="key"
            style="width: 100%; border: 1px solid #d0d0d0; border-radius: 5px;"
        >
          <template v-slot:item.dataAggiornamento="{ item }">
            <p v-if="item.dataAggiornamento">{{ formattaDataOra(item.dataAggiornamento) }}</p>
            <p v-else>-</p>
          </template>
          <template v-slot:item.palletAperto="{ item }">
            <v-chip v-if="item.palletAperto" color="green" variant="flat">SI</v-chip>
            <v-chip v-else color="red" variant="flat">NO</v-chip>
          </template>
          <template v-slot:item.palletSpedito="{ item }">
            <v-chip v-if="item.palletSpedito" color="blue" variant="flat">SI</v-chip>
            <v-chip v-else color="grey" variant="flat">NO</v-chip>
          </template>
        </v-data-table>
      </div>

      <v-alert
          v-if="matches.length === 0 && missing.length > 0"
          type="error"
          variant="tonal"
          style="margin-top: 20px;"
      >
        Nessun collo trovato. Verifica gli AWB e il periodo selezionato.
        <v-btn variant="outlined" @click="step = 'input'" style="margin-top: 10px;">INDIETRO</v-btn>
      </v-alert>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";

const axios = require("axios");
const {DateTime} = require("luxon");

export default {
  name: "BulkRemoval",

  setup() {
    return {
      dateTime: DateTime,
    };
  },

  data() {
    return {
      step: 'input',
      awbText: '',
      dataInizio: null,
      dataFine: null,
      nascondiColli: true,
      loading: false,
      deleting: false,
      matches: [],
      missing: [],
      selected: [],
      headers: [
        {title: "AWB CERCATO", value: "searchedAwb", sortable: true},
        {title: "AWB REGISTRATO", value: "outgoingTrackingNumber", sortable: true},
        {title: "BANCALE", value: "palletId", sortable: true},
        {title: "CORRIERE", value: "corriere", sortable: true},
        {title: "DATA AGGIORNAMENTO", value: "dataAggiornamento", sortable: true},
        {title: "APERTO", value: "palletAperto", sortable: false},
        {title: "SPEDITO", value: "palletSpedito", sortable: false},
      ],
    };
  },

  computed: {
    canSearch() {
      return this.awbText.trim().length > 0 && this.dataInizio !== null && this.dataFine !== null;
    },
  },

  methods: {
    formatDate(inputDate) {
      if (inputDate === null) {
        return "nessuna data selezionata";
      }
      const date = new Date(inputDate);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day} / ${month} / ${year}`;
    },
    formattaDataOra(date) {
      return this.dateTime
          .fromISO(date + "Z")
          .setZone("Europe/Rome")
          .setLocale("it")
          .toFormat("dd/MM/yyyy | HH:mm:ss");
    },
    async searchAwbs() {
      this.loading = true;
      const awbList = [...new Set(
          this.awbText
              .split('\n')
              .map(s => s.trim())
              .filter(s => s.length > 0)
      )];

      const payload = {
        awbList: awbList,
        dataInizio: DateTime.fromJSDate(this.dataInizio).setZone("Europe/Rome").toFormat("yyyy-MM-dd"),
        dataFine: DateTime.fromJSDate(this.dataFine).setZone("Europe/Rome").toFormat("yyyy-MM-dd"),
      };

      try {
        const response = await axios.post(
            `${this.$apiBaseUrl}/contetPallet/bulk/search`,
            payload,
            {headers: {Authorization: `Bearer ${this.$keycloak.token}`}}
        );
        this.matches = response.data.matches.map(m => ({
          ...m,
          key: `${m.palletId}_${m.outgoingTrackingNumber}`,
        }));
        this.missing = response.data.missing;
        this.selected = [];
        this.step = 'preview';
      } catch (error) {
        console.error(error);
        Swal.fire({icon: "error", title: "Errore", text: "Errore nella ricerca"});
      }
      this.loading = false;
    },
    selectAll() {
      this.selected = [...this.matches];
    },
    async confirmRemoval() {
      const result = await Swal.fire({
        title: "ATTENZIONE",
        html: `Stai per rimuovere <strong>${this.selected.length}</strong> colli dai bancali.`
            + (this.nascondiColli ? `<br>I colli verranno marcati come NASCOSTO e le scansioni IN_USCITA corrispondenti verranno eliminate.` : '')
            + `<br><br>Questa operazione non è reversibile.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "green",
        cancelButtonColor: "red",
        confirmButtonText: "CONFERMA",
        cancelButtonText: "ANNULLA",
      });

      if (!result.isConfirmed) return;

      this.deleting = true;
      const payload = {
        items: this.selected.map(m => ({
          palletId: m.palletId,
          outgoingTrackingNumber: m.outgoingTrackingNumber,
        })),
        nascondiColli: this.nascondiColli,
      };

      try {
        await axios.post(
            `${this.$apiBaseUrl}/contetPallet/bulk/delete`,
            payload,
            {headers: {Authorization: `Bearer ${this.$keycloak.token}`}}
        );
        Swal.fire({
          icon: "success",
          title: "Completato",
          text: `${this.selected.length} colli rimossi con successo`,
          timer: 3000,
          showConfirmButton: false,
        });
        // Remove deleted items from matches
        const deletedKeys = new Set(this.selected.map(s => s.key));
        this.matches = this.matches.filter(m => !deletedKeys.has(m.key));
        this.selected = [];
        if (this.matches.length === 0) {
          this.awbText = '';
          this.dataInizio = null;
          this.dataFine = null;
          this.step = 'input';
        }
      } catch (error) {
        console.error(error);
        Swal.fire({icon: "error", title: "Errore", text: "Errore nella rimozione"});
      }
      this.deleting = false;
    },
  },
};
</script>

<style scoped>
.v-btn--variant-outlined {
  border: thin solid #d0d0d0;
  box-shadow: 2px 2px 2px rgba(0, 0, 0, 0.2);
}
</style>
